import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { useUtmCapture } from "@/hooks/useUtmCapture";
import { HelmetProvider } from "react-helmet-async";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import SettingsProfile from "./pages/SettingsProfile";
import SettingsPreferences from "./pages/SettingsPreferences";
import SettingsReminders from "./pages/SettingsReminders";
import SettingsAccount from "./pages/SettingsAccount";
import InboxCheck from "./pages/InboxCheck";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import SettingsLegal from "./pages/SettingsLegal";
import Unsubscribe from "./pages/Unsubscribe";
import Goodbye from "./pages/Goodbye";
import UpgradeSuccess from "./pages/UpgradeSuccess";
import Pricing from "./pages/Pricing";

const queryClient = new QueryClient();

const UtmCaptureWrapper = ({ children }: { children: React.ReactNode }) => {
  useUtmCapture();
  return <>{children}</>;
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
        <Toaster />
        <Sonner />
        <UtmCaptureWrapper>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute requireOnboarding={false}>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inbox-check"
              element={
                <ProtectedRoute>
                  <InboxCheck />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/profile"
              element={
                <ProtectedRoute>
                  <SettingsProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/preferences"
              element={
                <ProtectedRoute>
                  <SettingsPreferences />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/reminders"
              element={
                <ProtectedRoute>
                  <SettingsReminders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/account"
              element={
                <ProtectedRoute>
                  <SettingsAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/legal"
              element={
                <ProtectedRoute>
                  <SettingsLegal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upgrade-success"
              element={
                <ProtectedRoute>
                  <UpgradeSuccess />
                </ProtectedRoute>
              }
            />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/goodbye" element={<Goodbye />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        </UtmCaptureWrapper>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
